import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/onboarding/buildEstimateResultValues";
import type { GoalId, QuizAnswers } from "@components/OnboardingWizard/types";
import type {
  OnboardingV3GoalType,
  OnboardingV3ResultValues,
} from "./onboardingV3Types";

const I18n = I18nUtil.createScoped(Translations);

export interface MoneyLeakBucketPercents {
  fixed: number;
  save: number;
  spending: number;
}

/** Take-home rate by pre-tax income tier (fe-spec §C.1). */
export const resolveTakeHomeRate = (annualIncome: number): number => {
  if (annualIncome < 60_000) {
    return 0.8;
  }
  if (annualIncome < 120_000) {
    return 0.76;
  }
  if (annualIncome < 250_000) {
    return 0.72;
  }
  return 0.66;
};

const DEFAULT_YOURS_BUCKETS: MoneyLeakBucketPercents = {
  fixed: 62,
  save: 6,
  spending: 32,
};

const DEFAULT_TARGET_BUCKETS: MoneyLeakBucketPercents = {
  fixed: 55,
  save: 20,
  spending: 25,
};

export interface PortfolioRiskSliceSpec {
  id: string;
  weight: number;
  label: string;
}

const MOCK_PORTFOLIO_BEFORE: PortfolioRiskSliceSpec[] = [
  { id: "top", weight: 34, label: I18n.t("Biggest holding") },
  { id: "other-us", weight: 28, label: I18n.t("Other US") },
  { id: "intl", weight: 22, label: I18n.t("International") },
  { id: "bonds", weight: 16, label: I18n.t("Bonds & cash") },
];

const MOCK_PORTFOLIO_AFTER: PortfolioRiskSliceSpec[] = [
  { id: "top", weight: 12, label: I18n.t("Biggest holding") },
  { id: "other-us", weight: 30, label: I18n.t("Other US") },
  { id: "intl", weight: 28, label: I18n.t("International") },
  { id: "bonds", weight: 30, label: I18n.t("Bonds & cash") },
];

export const buildEstimateResultValues = (input: {
  goalId: GoalId | string;
  answers: QuizAnswers;
}): OnboardingV3ResultValues | null => {
  const goalType = input.goalId.trim() as GoalId;
  const { answers } = input;
  const income = Math.max(0, answers.income);
  const expenses = Math.max(0, answers.expenses);
  const takeHome = income * resolveTakeHomeRate(income);
  const monthlyTakeHome = takeHome / 12;

  switch (goalType) {
    case "money-leak": {
      const targetSavePct = 20;
      const monthlyTargetSave = Math.round(
        (monthlyTakeHome * targetSavePct) / 100,
      );
      const annualLeak = Math.max(
        0,
        Math.round(monthlyTargetSave * 12 - (takeHome - expenses * 12)),
      );
      return {
        goalType: "spending",
        values: {
          mode: "estimate_two_bucket",
          hero: {
            annualSaveOpportunity: annualLeak || 15_120,
            monthlySaveOpportunity: monthlyTargetSave || 1_260,
          },
          chart: {
            current: [
              {
                bucket: "expenses",
                amount: expenses,
                ratePct: DEFAULT_YOURS_BUCKETS.spending,
              },
              {
                bucket: "remaining",
                amount: monthlyTargetSave || 1_260,
                ratePct: DEFAULT_YOURS_BUCKETS.save,
              },
            ],
            target: [
              {
                bucket: "fixed",
                amount: 0,
                ratePct: DEFAULT_TARGET_BUCKETS.fixed,
              },
              {
                bucket: "save",
                amount: monthlyTargetSave || 1_260,
                ratePct: DEFAULT_TARGET_BUCKETS.save,
              },
              {
                bucket: "spending",
                amount: 0,
                ratePct: DEFAULT_TARGET_BUCKETS.spending,
              },
            ],
          },
          annualSavings: annualLeak || 15_120,
          monthlySavings: monthlyTargetSave || 1_260,
          yoursBuckets: DEFAULT_YOURS_BUCKETS,
          targetBuckets: DEFAULT_TARGET_BUCKETS,
          goalRampLine1: "Get your saving to 20% of what lands",
          goalRampLine2: `$${(monthlyTargetSave || 1_260).toLocaleString("en-US")} a month`,
        },
      };
    }
    case "portfolio-risk": {
      const invest = Math.max(0, answers.invest);
      const topWeight =
        invest > 0
          ? Math.min(
              45,
              Math.max(28, Math.round((invest / (invest + answers.cash)) * 40)),
            )
          : 34;
      const withEdTopWeight = Math.max(12, Math.round(topWeight * 0.35));
      return {
        goalType: "invest",
        values: {
          hero: {
            topHoldingName: "Largest visible holding",
            topHoldingWeightPct: topWeight,
            guardrailWeightPct: 15,
            alreadyWithinGuardrail: topWeight <= 15,
          },
          chart: {
            before: MOCK_PORTFOLIO_BEFORE.map((slice, index) => ({
              key: slice.id,
              label: slice.label,
              marketValue: (index === 0 ? topWeight : slice.weight) * 1000,
              weightPct: index === 0 ? topWeight : slice.weight,
              highlighted: index === 0,
            })),
            after: MOCK_PORTFOLIO_AFTER.map((slice, index) => ({
              key: slice.id,
              label: slice.label,
              weightPct: index === 0 ? withEdTopWeight : slice.weight,
              highlighted: index === 0,
            })),
            caption: "Same money, spread differently",
          },
          backtest: null,
          topWeight,
          withEdTopWeight,
          beforeSlices: MOCK_PORTFOLIO_BEFORE.map((slice, index) =>
            index === 0 ? { ...slice, weight: topWeight } : slice,
          ),
          afterSlices: MOCK_PORTFOLIO_AFTER.map((slice, index) =>
            index === 0 ? { ...slice, weight: withEdTopWeight } : slice,
          ),
          goalRampLine1:
            "Bring single-name concentration into your 15% guardrail",
          goalRampLine2: "Staged over 6 months",
        },
      };
    }
    default:
      return null;
  }
};

/**
 * @deprecated generate 失败应停在 loading-error，不再用 estimate mock 进 result。
 * 保留供 harness / fixture；业务路径勿再调用。
 */
export const isUnsupportedGoalTypeError = (
  error: unknown,
  goalType: OnboardingV3GoalType,
): boolean => {
  if (!(error instanceof Error)) {
    return goalType === "spending" || goalType === "invest";
  }
  const message = error.message.toLowerCase();
  return (
    (goalType === "spending" || goalType === "invest") &&
    (message.includes("goaltype") ||
      message.includes("goal_type") ||
      message.includes("enum") ||
      message.includes("400"))
  );
};

export const MOCK_MONEY_LEAK_BUCKETS = {
  yours: DEFAULT_YOURS_BUCKETS,
  target: DEFAULT_TARGET_BUCKETS,
} as const;

export const MOCK_PORTFOLIO_RISK_SLICES = {
  before: MOCK_PORTFOLIO_BEFORE,
  after: MOCK_PORTFOLIO_AFTER,
  topWeight: 34,
  withEdTopWeight: 12,
} as const;
